import { supabase } from "@/integrations/supabase/client";
import { CareerMatchReport, IntakeFormData, RoleMatch } from "@/types/careerMatch";

export class CareerMatchService {
  
  /**
   * Create a new career match report
   */
  static async createReport(
    userId: string, 
    intakeData: IntakeFormData,
    reportName?: string
  ): Promise<{ report: CareerMatchReport | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('career_match_reports')
        .insert({
          user_id: userId,
          intake_data: intakeData as any,
          report_name: reportName,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      return { report: data as unknown as CareerMatchReport, error: null };
    } catch (error) {
      console.error('Error creating career match report:', error);
      return { report: null, error: error as Error };
    }
  }

  /**
   * Update a report with role matches
   */
  static async updateReportWithMatches(
    reportId: string,
    roleMatches: RoleMatch[],
    status: 'completed' | 'error' = 'completed'
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from('career_match_reports')
        .update({
          role_matches: roleMatches as any,
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', reportId);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating career match report:', error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Get user's career match reports
   */
  static async getUserReports(userId: string): Promise<{
    reports: CareerMatchReport[];
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('career_match_reports')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { reports: (data || []) as unknown as CareerMatchReport[], error: null };
    } catch (error) {
      console.error('Error fetching user reports:', error);
      return { reports: [], error: error as Error };
    }
  }

  /**
   * Get a specific report by ID
   */
  static async getReport(reportId: string): Promise<{
    report: CareerMatchReport | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('career_match_reports')
        .select('*')
        .eq('id', reportId)
        .single();

      if (error) throw error;

      return { report: data as unknown as CareerMatchReport, error: null };
    } catch (error) {
      console.error('Error fetching report:', error);
      return { report: null, error: error as Error };
    }
  }

  /**
   * Delete a report
   */
  static async deleteReport(reportId: string): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from('career_match_reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting report:', error);
      return { success: false, error: error as Error };
    }
  }

  /**
   * Generate career match report using backend endpoint
   */
  static async generateReport(intakeData: IntakeFormData): Promise<{
    roleMatches: RoleMatch[];
    error: Error | null;
  }> {
    try {
      const response = await supabase.functions.invoke('cmr-generate', {
        body: { intakeData }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return { roleMatches: response.data.roleMatches, error: null };
    } catch (error) {
      console.error('Error generating career match report:', error);
      return { roleMatches: [], error: error as Error };
    }
  }
}